import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  makeStyles,
  useTheme,
  useMediaQuery
} from "@material-ui/core";
import { CheckCircle, Error, Info } from "@material-ui/icons";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";

const useStyles = makeStyles((theme) => ({
  // ✅ Estilos modernos do modal seguindo padrão do projeto
  dialogTitle: {
    padding: theme.spacing(2, 3),
    flexShrink: 0,
    color: '#fff',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '1.2rem',
      color: 'inherit',
    }
  },
  ...createGradientStyles(theme),
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fafafa',
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: '1 1 auto',
    maxHeight: 'calc(100vh - 250px)',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'dark' ? '#2a2a2a' : '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.type === 'dark' ? '#555' : '#888',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.type === 'dark' ? '#777' : '#555',
      },
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5',
    borderTop: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0',
    flexShrink: 0,
  },
  progressContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  progressBar: {
    height: "28px",
    borderRadius: "14px",
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#e0e0e0',
    '& .MuiLinearProgress-bar': {
      borderRadius: "14px",
      transition: 'transform 0.4s linear',
    },
  },
  progressText: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
  },
  detailsList: {
    maxHeight: "300px",
    overflowY: "auto",
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#ffffff',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    border: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'dark' ? '#2a2a2a' : '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.type === 'dark' ? '#555' : '#888',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.type === 'dark' ? '#777' : '#555',
      },
    },
  },
  detailItem: {
    padding: theme.spacing(0.75, 1),
    fontSize: "0.875rem",
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#f5f5f5',
    },
  },
  statusIcon: {
    marginRight: theme.spacing(1),
    fontSize: '20px',
  },
  completedStatus: {
    color: theme.palette.success.main,
  },
  errorStatus: {
    color: theme.palette.error.main,
  },
  infoStatus: {
    color: theme.palette.info.main,
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  summaryBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#ffffff',
    borderRadius: theme.shape.borderRadius,
    border: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0',
  },
}));

const FixContactsLidModal = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colorModeContext = useContext(ColorModeContext);
  const useGradient = colorModeContext?.useGradient || theme.palette?.gradient || false;
  const { user, socket } = useContext(AuthContext);
  const [progress, setProgress] = useState({
    step: "idle",
    progress: 0,
    current: 0,
    total: 0,
    message: "",
    details: "",
  });
  const [isRunning, setIsRunning] = useState(false);
  const [details, setDetails] = useState([]);

  useEffect(() => {
    if (!open || !socket) return;

    const companyId = user.companyId;
    const eventName = `company-${companyId}-fix-contacts-lid-progress`;

    const handleProgress = (data) => {
      setProgress(data);
      
      // Adicionar detalhe à lista
      const timestamp = new Date().toLocaleTimeString();
      setDetails((prev) => [
        ...prev,
        {
          timestamp,
          step: data.step,
          message: data.message,
          details: data.details,
        },
      ]);

      // Se completou ou teve erro, parar
      if (data.step === "completed" || data.step === "error") {
        setIsRunning(false);
      }
    };

    socket.on(eventName, handleProgress);

    return () => {
      socket.off(eventName, handleProgress);
    };
  }, [open, socket, user.companyId]);

  const handleStart = async () => {
    try {
      setIsRunning(true);
      setProgress({
        step: "starting",
        progress: 0,
        current: 0,
        total: 0,
        message: "Iniciando...",
        details: "",
      });
      setDetails([]);

      await api.post("/contacts/fix-lid");
    } catch (err) {
      toastError(err);
      setIsRunning(false);
      setProgress((prev) => ({
        ...prev,
        step: "error",
        message: "Erro ao iniciar saneamento",
        details: err.response?.data?.message || err.message,
      }));
    }
  };

  const handleClose = () => {
    if (isRunning) {
      // Não permitir fechar enquanto está rodando
      return;
    }
    onClose();
    // Resetar estado após fechar
    setTimeout(() => {
      setProgress({
        step: "idle",
        progress: 0,
        current: 0,
        total: 0,
        message: "",
        details: "",
      });
      setDetails([]);
    }, 300);
  };

  const getStepLabel = (step) => {
    const labels = {
      idle: i18n.t("contacts.fixContactsLid.steps.idle") || "Aguardando",
      starting: i18n.t("contacts.fixContactsLid.steps.starting") || "Iniciando",
      identifying: i18n.t("contacts.fixContactsLid.steps.identifying") || "Identificando contatos",
      finding: i18n.t("contacts.fixContactsLid.steps.finding") || "Buscando contatos reais",
      merging: i18n.t("contacts.fixContactsLid.steps.merging") || "Mesclando contatos",
      completed: i18n.t("contacts.fixContactsLid.steps.completed") || "Concluído",
      error: i18n.t("contacts.fixContactsLid.steps.error") || "Erro",
    };
    return labels[step] || step;
  };

  const getStatusIcon = () => {
    if (progress.step === "completed") {
      return <CheckCircle className={`${classes.statusIcon} ${classes.completedStatus}`} />;
    }
    if (progress.step === "error") {
      return <Error className={`${classes.statusIcon} ${classes.errorStatus}`} />;
    }
    if (isRunning) {
      return <CircularProgress size={20} className={classes.statusIcon} />;
    }
    return <Info className={`${classes.statusIcon} ${classes.infoStatus}`} />;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? 0 : 4,
        }
      }}
    >
      <DialogTitle 
        className={`${classes.dialogTitle} ${useGradient ? classes.gradientBackground : classes.solidBackground}`}
      >
        {i18n.t("contacts.fixContactsLid.title") || "Saneamento de Contatos (LID)"}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.progressContainer}>
          <Box className={classes.progressText}>
            <Box className={classes.statusContainer}>
              {getStatusIcon()}
              <Typography variant="body1" component="span" style={{ fontWeight: 500 }}>
                {getStepLabel(progress.step)}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
              {progress.progress}%
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progress.progress}
            className={classes.progressBar}
            color="primary"
          />

          {progress.message && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 12, fontWeight: 500 }}>
              {progress.message}
            </Typography>
          )}

          {progress.details && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
              {progress.details}
            </Typography>
          )}

          {progress.total > 0 && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
              {progress.current} / {progress.total}
            </Typography>
          )}
        </Box>

        {details.length > 0 && (
          <Box className={classes.detailsList}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              {i18n.t("contacts.fixContactsLid.details") || "Detalhamento:"}
            </Typography>
            <List dense>
              {details.slice(-10).map((detail, index) => (
                <ListItem key={index} className={classes.detailItem}>
                  <ListItemText
                    primary={
                      <Typography variant="caption">
                        <strong>[{detail.timestamp}]</strong> {detail.message}
                      </Typography>
                    }
                    secondary={
                      detail.details && (
                        <Typography variant="caption" color="textSecondary">
                          {detail.details}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        {!isRunning && progress.step === "idle" && (
          <Button 
            onClick={handleStart} 
            color="primary" 
            variant="contained"
            style={{ 
              borderRadius: 4,
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 16px',
            }}
          >
            {i18n.t("contacts.fixContactsLid.start") || "Iniciar"}
          </Button>
        )}
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={isRunning}
          style={{ 
            borderRadius: 4,
            textTransform: 'none',
            fontWeight: 500,
            padding: '6px 16px',
          }}
        >
          {i18n.t("contacts.fixContactsLid.close") || "Fechar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FixContactsLidModal;
